import React, { useState, useEffect } from 'react';
import {
    Button,
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { iEnvSound } from '../../models/EnvSound';
import { ASSETS_PATH } from '../../constants';
import Swal from 'sweetalert2';



const EnvSounds: React.FC = () => {
    const navigate = useNavigate();
    const [envSounds, setEnvSounds] = useState<iEnvSound[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchEnvSounds = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token'); // Assuming token is in localStorage
                const response = await fetch(`${process.env.REACT_APP_API_URL}/env-sounds`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch environment sounds');
                }

                const data: { envSounds: iEnvSound[] } = await response.json();
                setEnvSounds(data.envSounds);
                setError(null);
            } catch (err) {
                if (err instanceof Error) {
                    setError(err.message);
                } else {
                    setError('An unknown error occurred');
                }
                setEnvSounds([]);
            } finally {
                setLoading(false);
            }
        };

        fetchEnvSounds();
    }, []);

    const handleEdit = (id: number) => {
        navigate(`/env-sound/${id}`);
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });
    
        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.REACT_APP_API_URL}/env-sounds/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });
    
                if (!response.ok) {
                    throw new Error('Failed to delete environment sound');
                }
    
                setEnvSounds(prevSounds => prevSounds.filter(sound => sound.env_sound_id !== id));
                Swal.fire('Deleted!', 'The environment sound has been deleted.', 'success');
            } catch (err) {
                const message = err instanceof Error ? err.message : 'An unknown error occurred during deletion';
                setError(message);
                Swal.fire('Error!', message, 'error');
            }
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <Typography variant="h4" gutterBottom component="div">
                    Environment Sounds
                </Typography>
                <Button variant="contained" onClick={() => navigate('/env-sound/0')}>Create New Env Sound</Button>
            </div>

            {loading && <CircularProgress />}
            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="environment sounds table">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Sound</TableCell>
                                <TableCell>Beacon ID</TableCell>                                
                                <TableCell>Loop</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {envSounds.map((sound) => (
                                <TableRow
                                    key={sound.env_sound_id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell component="th" scope="row">
                                        {sound.env_sound_id}
                                    </TableCell>
                                    <TableCell>{sound.env_sound_name}</TableCell>
                                    <TableCell>
                                        {sound.env_sound && (
                                            <audio controls src={`${ASSETS_PATH}/${sound.env_sound}`} style={{ height: '30px' }}>
                                                Your browser does not support the audio element.
                                            </audio>
                                        )}
                                    </TableCell>
                                    <TableCell>{sound.env_start_beacon_id}</TableCell>                                    
                                    <TableCell>{sound.env_loop}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={() => handleEdit(sound.env_sound_id!)}>Edit</Button>
                                        <Button size="small" color="error" onClick={() => handleDelete(sound.env_sound_id!)}>Delete</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Container>
    );
};

export default EnvSounds;